// TeamLeaderboard: classifiche dei compagni di squadra nel club EA.
// Tab Squadra dentro Mondo FC della PlayerArea. Riusa la RPC
// team_player_position_stats con position=null per avere stat aggregate
// di tutti i player con EA handle.
//
// Categorie disponibili (chips): Goal · Assist · MOTM · Rating · Win rate
// · Match. Sotto, podio top 3 + lista completa con highlight della riga
// del player corrente. Banner "Tu sei #X" in alto quando applicabile.

import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { Crown, TrendingUp, Trophy, Users } from "lucide-react";
import { Avatar, AvatarFallback } from "@v2/components/ui/avatar";
import { Badge } from "@v2/components/ui/badge";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@v2/components/ui/card";
import { cn } from "@v2/lib/utils";
import { supabaseV2 } from "../../lib/supabase";
import { EmptyState, SectionLoading } from "../../components/state";
import type { PositionStats } from "../players/comparison-math";
import { HowItCountsDialog } from "./HowItCountsDialog";

// Soglia minima di match per essere considerati: chi non ha giocato
// abbastanza viene escluso dalla classifica (ma resta visibile come
// "non eleggibile" se vogliamo aggiungere uno snippet futuro).
const MIN_MATCHES = 3;

type LeaderKind =
  | "goals"
  | "goals_per_match"
  | "assists"
  | "assists_per_match"
  | "mom"
  | "avg_rating"
  | "win_rate"
  | "matches";

type LeaderDef = {
  id: LeaderKind;
  labelKey: string;
  fmt: (s: PositionStats) => string;
  pick: (s: PositionStats) => number;
};

const LEADERS: LeaderDef[] = [
  { id: "goals",             labelKey: "teamLb.kind.goals",            pick: (s) => s.goals,              fmt: (s) => String(s.goals) },
  { id: "assists",           labelKey: "teamLb.kind.assists",          pick: (s) => s.assists,            fmt: (s) => String(s.assists) },
  { id: "mom",               labelKey: "teamLb.kind.mom",              pick: (s) => s.mom,                fmt: (s) => String(s.mom) },
  { id: "avg_rating",        labelKey: "teamLb.kind.rating",           pick: (s) => s.top10_avg_rating || s.avg_rating, fmt: (s) => (s.top10_avg_rating || s.avg_rating).toFixed(2) },
  { id: "win_rate",          labelKey: "teamLb.kind.winRate",          pick: (s) => s.win_rate,           fmt: (s) => `${s.win_rate.toFixed(0)}%` },
  { id: "goals_per_match",   labelKey: "teamLb.kind.goalsPerMatch",    pick: (s) => s.goals_per_match,    fmt: (s) => s.goals_per_match.toFixed(2) },
  { id: "assists_per_match", labelKey: "teamLb.kind.assistsPerMatch",  pick: (s) => s.assists_per_match,  fmt: (s) => s.assists_per_match.toFixed(2) },
  { id: "matches",           labelKey: "teamLb.kind.matches",          pick: (s) => s.matches,            fmt: (s) => String(s.matches) },
];

export function TeamLeaderboard({
  teamId,
  playerId,
  onOpenPlayer,
}: {
  teamId: string;
  playerId: string;
  onOpenPlayer?: (id: string) => void;
}) {
  const { t } = useTranslation("mondofc");
  const [kind, setKind] = useState<LeaderKind>("goals");

  // 1) Tutti i player del team con EA handle (per filtrarli nella RPC).
  const rosterQuery = useQuery({
    queryKey: ["team-lb-roster", teamId],
    queryFn: async () => {
      const { data, error } = await supabaseV2
        .from("players")
        .select("id, ea_account_handle, ea_sports_id")
        .eq("team_id", teamId);
      if (error) throw error;
      return (data || []) as Array<{ id: string; ea_account_handle: string | null; ea_sports_id: string | null }>;
    },
    staleTime: 60_000,
  });

  const eligibleIds = useMemo(
    () => (rosterQuery.data || [])
      .filter((p) => p.ea_account_handle || p.ea_sports_id)
      .map((p) => p.id),
    [rosterQuery.data],
  );

  // 2) Stat aggregate per tutti i player con EA handle, su tutti i ruoli
  //    (position=null). Tutto il period (period=null).
  const statsQuery = useQuery({
    queryKey: ["team-lb-stats", teamId, eligibleIds.length],
    enabled: eligibleIds.length > 0,
    queryFn: async () => {
      const { data, error } = await supabaseV2.rpc("team_player_position_stats", {
        p_team_id: teamId,
        p_player_ids: eligibleIds,
        p_position: null,
        p_period_days: null,
      });
      if (error) throw error;
      return data as { players: PositionStats[] };
    },
    staleTime: 30_000,
  });

  const rows = useMemo(
    () => (statsQuery.data?.players || []).filter((s) => s.matches >= MIN_MATCHES),
    [statsQuery.data],
  );

  const sortedByCurrentKind = useMemo(() => {
    const def = LEADERS.find((l) => l.id === kind)!;
    return [...rows].sort((a, b) => def.pick(b) - def.pick(a));
  }, [rows, kind]);

  const myRow = sortedByCurrentKind.find((r) => r.player_id === playerId) ?? null;
  const myRank = myRow ? sortedByCurrentKind.indexOf(myRow) + 1 : null;

  if (rosterQuery.isLoading || statsQuery.isLoading) return <SectionLoading />;

  if (eligibleIds.length === 0 || rows.length === 0) {
    return (
      <Card className="shadow-card">
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <Users className="h-4 w-4 text-muted-foreground" />
            {t("teamLb.title")}
          </CardTitle>
          <CardDescription>{t("teamLb.desc", { min: MIN_MATCHES })}</CardDescription>
        </CardHeader>
        <CardContent className="py-8">
          <EmptyState text={t("teamLb.empty", { min: MIN_MATCHES })} />
        </CardContent>
      </Card>
    );
  }

  const def = LEADERS.find((l) => l.id === kind)!;

  return (
    <Card className="shadow-card">
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between gap-2 flex-wrap">
          <div>
            <CardTitle className="text-base flex items-center gap-2">
              <Trophy className="h-4 w-4 text-primary" />
              {t("teamLb.title")}
            </CardTitle>
            <CardDescription>{t("teamLb.desc", { min: MIN_MATCHES })}</CardDescription>
          </div>
          <HowItCountsDialog variant="leaderboard" />
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        {/* Selettore categoria */}
        <div className="flex flex-wrap gap-1.5">
          {LEADERS.map((l) => (
            <button
              key={l.id}
              type="button"
              onClick={() => setKind(l.id)}
              className={cn(
                "rounded-full border px-3 py-1 text-xs font-medium transition-smooth",
                kind === l.id
                  ? "border-primary bg-primary/10 text-primary"
                  : "bg-card hover:bg-muted/60 text-foreground/80",
              )}
            >
              {t(l.labelKey)}
            </button>
          ))}
        </div>

        {/* Banner "Tu sei #X" */}
        {myRow && myRank && (
          <div className="rounded-lg border border-primary/30 bg-primary/5 px-3 py-2 flex items-center gap-2">
            <TrendingUp className="h-4 w-4 text-primary shrink-0" />
            <p className="text-sm">
              {t("teamLb.youAre", {
                rank: myRank,
                total: sortedByCurrentKind.length,
                metric: t(def.labelKey),
                value: def.fmt(myRow),
              })}
            </p>
          </div>
        )}

        {/* Podio top 3 */}
        {sortedByCurrentKind.length >= 1 && (
          <div className="grid grid-cols-3 gap-2">
            {[1, 0, 2].map((podiumIdx) => {
              const r = sortedByCurrentKind[podiumIdx];
              if (!r) return <div key={podiumIdx} />;
              const place = podiumIdx + 1;
              const isMe = r.player_id === playerId;
              const tone =
                place === 1
                  ? "bg-amber-500/10 border-amber-500/40"
                  : place === 2
                    ? "bg-slate-400/10 border-slate-400/40"
                    : "bg-amber-700/10 border-amber-700/40";
              return (
                <button
                  key={r.player_id}
                  type="button"
                  onClick={() => onOpenPlayer?.(r.player_id)}
                  disabled={!onOpenPlayer}
                  className={cn(
                    "rounded-xl border p-2.5 text-center space-y-1",
                    tone,
                    place === 1 && "ring-2 ring-amber-500/50",
                    isMe && "ring-2 ring-primary",
                    onOpenPlayer && "hover:opacity-90 transition-opacity",
                  )}
                >
                  <div className="flex items-center justify-center gap-1">
                    {place === 1 && <Crown className="h-3.5 w-3.5 text-amber-500" />}
                    <span className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">
                      #{place}
                    </span>
                  </div>
                  <Avatar className="h-10 w-10 mx-auto">
                    <AvatarFallback className="text-[10px] font-semibold">
                      {r.display_name.split(/\s+/).map((p) => p[0]).slice(0, 2).join("").toUpperCase()}
                    </AvatarFallback>
                  </Avatar>
                  <p className="text-[11px] font-medium truncate">{r.display_name}</p>
                  <p className="text-base font-bold tabular-nums text-primary leading-tight">
                    {def.fmt(r)}
                  </p>
                </button>
              );
            })}
          </div>
        )}

        {/* Lista completa */}
        <div className="space-y-1 pt-1 border-t border-border/50">
          <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold mb-1.5">
            {t("teamLb.fullList")}
          </p>
          {sortedByCurrentKind.map((r, idx) => {
            const rank = idx + 1;
            const isMe = r.player_id === playerId;
            return (
              <button
                key={r.player_id}
                type="button"
                onClick={() => onOpenPlayer?.(r.player_id)}
                disabled={!onOpenPlayer}
                className={cn(
                  "w-full flex items-center gap-2 rounded-lg border bg-background/40 px-3 py-2 transition-colors",
                  isMe && "border-primary bg-primary/10",
                  onOpenPlayer && "hover:bg-muted/40",
                )}
              >
                <span
                  className={cn(
                    "w-6 text-center text-[11px] tabular-nums font-bold shrink-0",
                    rank <= 3 ? "text-primary" : "text-muted-foreground",
                  )}
                >
                  #{rank}
                </span>
                <Avatar className="h-7 w-7 shrink-0">
                  <AvatarFallback className="text-[9px] font-semibold">
                    {r.display_name.split(/\s+/).map((p) => p[0]).slice(0, 2).join("").toUpperCase()}
                  </AvatarFallback>
                </Avatar>
                <div className="flex-1 min-w-0 text-left">
                  <p className="text-sm font-medium truncate flex items-center gap-1.5">
                    <span className="truncate">{r.display_name}</span>
                    {isMe && (
                      <Badge variant="outline" className="text-[9px] uppercase tracking-wider border-primary text-primary">
                        {t("teamLb.youBadge")}
                      </Badge>
                    )}
                    {r.is_guest && (
                      <span className="text-[9px] uppercase tracking-wider text-muted-foreground shrink-0">
                        {t("teamLb.guestTag")}
                      </span>
                    )}
                  </p>
                  <p className="text-[11px] text-muted-foreground tabular-nums">
                    {r.matches} {t("teamLb.matchesUnit")}
                  </p>
                </div>
                <span className="text-base font-bold tabular-nums text-primary shrink-0">
                  {def.fmt(r)}
                </span>
              </button>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
}
